<template>
    <div>

        <title-card
            :style="'border-left: 5px solid ' + markerColor"
            :side-icon="titleIcon"
            :title="title"
            :help="help"
            @click.native="editClicked">

            <template v-slot:buttons v-if="editable">
                <v-btn v-show="selectableSegment" text @click.stop="segmentPickerDialogModel=true">SELECT</v-btn>
            </template>

            <alert type="warning" v-show="!isSegmentDefined">{{ emptySegmentWarning }}</alert>

            <v-tooltip top color="primary"
                       :disabled="!editable"
                       absolute
                       :position-x="mouseX"
                       :position-y="mouseY"
                       :open-delay="500">

                <template v-slot:activator="{ on }">

                    <v-card-text v-on="on" class="pa-0" v-show="isSegmentDefined" @mouseover="mouseOver" @mouseout="mouseOut">
                        <v-row no-gutters>

                            <v-col class="pa-0" cols="12" v-if="isQueryDefined">

                                <segment-query-visualizer
                                    :data-variables="dataVariables"
                                    :query="displayQuery"
                                ></segment-query-visualizer>
                            </v-col>

                            <v-col class="pa-0" cols="12" v-if="isFilterDefined">
                                <segment-filters-visualizer
                                    class="pt-0"
                                    :filter="displayFilter"
                                ></segment-filters-visualizer>
                            </v-col>
                        </v-row>
                    </v-card-text>

                </template>
                Click anywhere to edit
            </v-tooltip>

        </title-card>

        <!-- SAMPLE DEFINITION BUILDER DIALOG -->
        <v-dialog
            v-model="builderOpenModel"
            fullscreen
            hide-overlay
            transition="fade-transition"
        >
            <v-card v-if="builderOpenModel" color="grey darken-4">
                <v-card-text>

                    <segment-builder
                        :json_query="data.json_query"
                        :json_filter="data.json_filter"
                        @cancel="cancelDefinition"
                        @save="saveDefinition"
                        :form="false"
                        :target-completes="targetCompletes"
                        :disable-sub-panel-filtering="disableSubPanelFiltering"
                        :show-business-rules="showBusinessRules"
                        saveButtonText="Update"
                        @change:selected-sub-panels="selectedSubPanelsChanged"
                    ></segment-builder>

                </v-card-text>
            </v-card>

        </v-dialog>

        <segment-picker-dialog
            v-model="segmentPickerDialogModel"
            @select="sampleDefinitionSelected"
            @close="segmentPickerDialogModel=false"
        ></segment-picker-dialog>
    </div>
</template>

<script>

import Store from '../store';
import {mapState, mapActions} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import ToolbarPage from '../../../components/UI/ToolbarPage';
import VueJsonPretty from 'vue-json-pretty';
import SegmentFiltersVisualizer from './SegmentFiltersVisualizer';
import SegmentQueryVisualizer from './SegmentQueryVisualizer';
import SegmentBuilder from './SegmentBuilder';
import Alert from "../../../components/UI/Alert";
import SegmentPickerDialog from "../../../components/UI/SegmentPickerDialog";
import TitleCard from "../../../components/UI/TitleCard";
import {colorTools} from "../../../components/mixins/colorTools";

export default {

    mixins: [activeSessionChecker, colorTools],

    components: {
        SegmentBuilder,
        Alert,
        ToolbarPage,
        VueJsonPretty,
        SegmentFiltersVisualizer,
        SegmentQueryVisualizer,
        SegmentPickerDialog,
        TitleCard

    },

    props: {
        jsonQuery: {
            type: Object | null,
            required: false,
            default: '{}',
        },
        jsonFilter: {
            type: Object | null,
            required: false,
            default: '{}'
        },
        emptySegmentWarning: {
            type: String,
            required: false,
            default: "This segment has not been defined"
        },
        name: {
            type: String,
            required: false,
            default: 'My sample segment',
        },
        created_by: {
            required: false,
            type: Object,
            default: () => null
        },
        targetCompletes: {
            required: false,
            type: Number | null,
            default: null,
        },
        color: {
            required: false,
            type: String,
            default: 'grey darken-2'
        },
        title: {
            required: false,
            type: String,
            default: 'Segment'
        },
        titleColor: {
            required: false,
            type: String,
            default: 'light-blue darken-4'
        },
        titleIcon: {
            required: false,
            type: String,
            default: 'mdi-filter-outline'
        },
        definitionBuilderOpen: {
            required: false,
            type: Boolean,
            default: false
        },
        showBusinessRules: {
            required: false,
            type: Boolean,
            default: true
        },
        editable: {
            required: false,
            type: Boolean,
            default: true,
        },
        nonEditableInfo: {
            required: false,
            type: String,
            default: 'This segment is not editable here. It is shown for your information only.'
        },
        selectableSegment: {
            required: false,
            type: Boolean,
            default: true
        },
        disableSubPanelFiltering: {
            required: false,
            type: Boolean,
            default: false,
        },
        help: {
            type: String | null,
            required: false,
            default: null
        }
    },

    data() {
        return {
            builderOpenModel: false,
            typeIcons: [
                {type: "radio", icon: "radio_button_checked"},
                {type: "checkbox", icon: "check_circle_outline"},
                {type: "text", icon: "text_format"},
                {type: "date", icon: "access_time"},
                {type: "email", icon: "mail"}
            ],
            data: {
                name: 'My segment',
                json_query: [],
                json_filter: [],
            },
            activityFilter: null,
            businessRules: null,
            filtersMenuOpen: false,
            showOptionsTabModel: true,
            isMouseOver: false,
            segmentPickerDialogModel: false,
            mouseX: null,
            mouseY: null
        }
    },

    mounted() {
        this.buildFormFromProps();
    },

    watch: {

        definitionBuilderOpen(newValue) {
            this.builderOpenModel = newValue;
        },

        jsonQuery(newValue) {
            this.buildFormFromProps();
        },

        jsonFilter(newValue) {
            this.buildFormFromProps();
        }
    },

    computed: {

        markerColor() {
            if (this.isSegmentDefined) {
                return this.convertToRgba(this.titleColor, 0.7);
            } else {
                return this.convertToRgba('#FF0000', 0.7);
            }
        },

        isSegmentDefined() {
            return this.isQueryDefined || this.isFilterDefined;
        },

        isQueryDefined() {
            return this.data.json_query.conditions && this.data.json_query.conditions.length > 0;
        },

        isFilterDefined() {
            if (this.displayFilter.active) return true;
            if (this.displayFilter.excludeCategoriesActive) return true;
            if (this.displayFilter.includeSubpanelsActive) return true;
            return false;
        },

        displayQuery() {
            return this.data.json_query;
        },

        displayFilter() {
            return this.data.json_filter;
        },

        items() {

            let items = [];

            this.dataVariables.forEach((dataVariable) => {

                // TODO Refactor out
                this.typeIcons.forEach((icon) => {
                    if (icon.type === dataVariable.type) {
                        dataVariable.icon = icon.icon;
                    }
                });

                items.push(dataVariable);
            });
            return items;
        },

        // Root store
        ...mapState([
            'dataVariables',
        ]),

        ...mapState('Segments', [
            'matches',
            'queryRunning',
            'executionTime'
        ]),
    },

    methods: {

        ...mapActions('Segments', [
            'validateElasticSearchQuery'
        ]),

        buildFormFromProps() {
            this.data.id = this.id;
            this.data.name = this.name;
            this.data.shared = this.shared;
            this.data.type = this.type;
            this.data.json_query = this.jsonQuery ? this.jsonQuery : [];
            this.data.json_filter = this.jsonFilter ? this.jsonFilter : [];
        },

        cancelDefinition() {
            this.builderOpenModel = false;
            this.$emit('cancel');
        },

        saveDefinition(segment) {
            this.builderOpenModel = false;
            this.data.json_query = segment.json_query;
            this.data.json_filter = segment.json_filter;
            this.$emit('change', this.data);
        },

        editClicked() {
            if (this.editable) {
                this.builderOpenModel = true;
            } else {
                Bus.$emit('flash-message', {message: this.nonEditableInfo});
            }
        },

        sampleDefinitionSelected(segment) {
            this.segmentPickerDialogModel = false;
            this.data.json_query = segment.json_query;
            this.data.json_filter = segment.json_filter;
            this.$emit('change', this.data);
        },

        mouseOver(bool) {
            this.$el.addEventListener('mousemove', this.mouseMove, false);
            this.isMouseOver = bool;
        },

        mouseOut() {
            this.$el.removeEventListener('mousemove', this.mouseMove);
        },

        selectedSubPanelsChanged(ids) {
            this.$emit('change:selected-sub-panels', ids);
        },

        mouseMove(event) {
            this.mouseX = event.clientX;
            this.mouseY = event.clientY;
        },
    },

    created() {
        if (!this.$store.state.Segments) {
            this.$store.registerModule('Segments', Store);
        }

        this.buildFormFromProps();

        this.builderOpenModel = this.definitionBuilderOpen;
    },
}

</script>

<style scoped>
.parent {
    display: flex;
    align-items: center;
    width: 100%;
}

.child {
    float: left;
}

</style>
